import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, RefreshCw, WifiOff } from 'lucide-react';
import { Button } from './ui/button';
import { SmartImage } from './SmartImage';
import { CinematicLoader } from './CinematicLoader';
import { AdvancedImageAnalyzer, ImageAnalysisResult } from './AdvancedImageAnalyzer';
import { apiService, HighlightEvent, ApiService } from '../services/api';

// Cache para eventos carregados
let cachedEvents: HighlightEvent[] | null = null;

function EnhancedHeroComponent() {
  const [events, setEvents] = useState<HighlightEvent[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [imageAnalysis, setImageAnalysis] = useState<Record<string, ImageAnalysisResult>>({});
  const [reducedMotion, setReducedMotion] = useState(false);

  // Detect user's motion preferences
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);
    
    const handleChange = () => setReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Load highlight events - NO FALLBACK
  const loadHighlightEvents = async () => {
    try {
      // Usar cache se disponível
      if (cachedEvents && cachedEvents.length > 0) {
       // console.log('🎬 [ENHANCED_HERO] Using cached events:', cachedEvents.length);
        setEvents(cachedEvents);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      //console.log('🎬 [ENHANCED_HERO] Starting to load highlight events...');
      
      // Set a timeout for API call
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Tempo limite excedido')), 8000)
      );
      
      const apiPromise = apiService.getHighlightEvents();
      
      const response = await Promise.race([apiPromise, timeoutPromise]);
      
      //console.log('🎬 [ENHANCED_HERO] API Response:', response);

      if (response.events && response.events.length > 0) {
        // Log each event for debugging
        // response.events.forEach((event, index) => {
        //   console.log(`🎬 [ENHANCED_HERO] Event ${index + 1}:`, {
        //     eventUrl: event.eventUrl,
        //     imageUrl: event.imageUrl,
        //     hasValidUrl: event.imageUrl && event.imageUrl.startsWith('http'),
        //     hasEventUrl: !!event.eventUrl
        //   });
        // });
        
        // Be more lenient with validation for debugging
        const validEvents = response.events.filter(event => 
          event.eventUrl && event.imageUrl
        );
        
        //console.log(`🎬 [ENHANCED_HERO] Filtered events: ${validEvents.length}/${response.events.length}`);
        
        if (validEvents.length > 0) {
          cachedEvents = validEvents; // Cache os eventos
          setEvents(validEvents);
          setError(null);
        } else {
          throw new Error('Nenhum evento em destaque encontrado');
        }
      } else {
        throw new Error('Nenhum evento em destaque disponível');
      }
    } catch (err) {
    //  console.log('🎬 [ENHANCED_HERO] API failed:', err.message);
      
      const errorMessage = err.message === 'Tempo limite excedido' 
        ? 'Conexão muito lenta. Verifique sua internet e tente novamente.'
        : 'Erro ao carregar eventos em destaque. Tente novamente em alguns instantes.';
      
      setError(errorMessage);
      setEvents([]);
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    loadHighlightEvents();
  }, []);

  // Enhanced auto-play
  useEffect(() => {
    if (!isAutoPlaying || events.length <= 1 || reducedMotion) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % events.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, events.length, reducedMotion]);

  // Handle image analysis results - memoized
  const handleImageAnalysis = useCallback((src: string, result: ImageAnalysisResult) => {
    if (src && result) {
      setImageAnalysis(prev => {
        if (prev[src]) return prev; // Não substituir se já existe
        return {
          ...prev,
          [src]: result
        };
      });
    }
  }, []);

  // Navigation functions - memoized
  const goToSlide = useCallback((index: number) => {
    if (index < 0 || index >= events.length) return;
    
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    
    // Resume autoplay after delay
    setTimeout(() => setIsAutoPlaying(true), 15000);
  }, [events.length]);

  const goToPrevious = useCallback(() => {
    const newIndex = (currentSlide - 1 + events.length) % events.length;
    goToSlide(newIndex);
  }, [currentSlide, events.length, goToSlide]);

  const goToNext = useCallback(() => {
    const newIndex = (currentSlide + 1) % events.length;
    goToSlide(newIndex);
  }, [currentSlide, events.length, goToSlide]);

  const handleEventClick = useCallback((eventUrl: string) => {
    if (eventUrl && eventUrl.startsWith('http')) {
      window.open(eventUrl, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    cachedEvents = null; // Limpar cache
    await loadHighlightEvents();
  }, []);

  // Get current event analysis safely - memoized
  const currentEvent = useMemo(() => events[currentSlide], [events, currentSlide]);
  const currentAnalysis = useMemo(() => 
    currentEvent?.imageUrl ? imageAnalysis[currentEvent.imageUrl] : null,
    [currentEvent?.imageUrl, imageAnalysis]
  );

  // Generate dynamic overlay classes based on analysis - memoized
  const dynamicOverlayClasses = useMemo(() => {
    if (!currentAnalysis) return 'hero-adaptive-gradient';
    
    const classes = ['hero-adaptive-gradient'];
    
    if (currentAnalysis.needsStrongOverlay) {
      classes.push('hero-strong-overlay');
    }
    
    if (currentAnalysis.colorTemperature === 'warm') {
      classes.push('hero-warm-compensation');
    } else if (currentAnalysis.colorTemperature === 'cool') {
      classes.push('hero-cool-compensation');
    }
    
    return classes.join(' ');
  }, [currentAnalysis]);

  // Memoize eventos para análise - apenas os primeiros 3
  const eventsToAnalyze = useMemo(() => events.slice(0, 3), [events]);

  // Loading state with cinematic loader - ALTURA BALANCEADA
  if (isLoading) {
    return (
      <section className="relative h-[40vh] sm:h-[45vh] lg:h-[50vh] min-h-[300px] max-h-[500px] overflow-hidden mt-18">
        <CinematicLoader type="hero" showShimmer={!reducedMotion} />
      </section>
    );
  }

  // Error state - show friendly error message with retry - ALTURA BALANCEADA
  if (error || events.length === 0) {
    return (
      <section className="relative h-[40vh] sm:h-[45vh] lg:h-[50vh] min-h-[300px] max-h-[500px] overflow-hidden mt-18 bg-gradient-to-br from-muted/50 to-background">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-6">
            <div className="card-elegant p-8 bg-card/95 backdrop-blur-sm">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-orange-50 dark:bg-orange-950/20 rounded-2xl border border-orange-200 dark:border-orange-800/30">
                <WifiOff className="w-8 h-8 text-orange-500" />
              </div>
              <h2 className="text-xl mb-4 text-orange-600 dark:text-orange-400">
                Eventos em destaque indisponíveis
              </h2>
              <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
                {error || 'Não foi possível carregar os eventos em destaque. Verifique sua conexão e tente novamente.'}
              </p>
              <Button 
                onClick={handleRetry}
                disabled={isRetrying}
                size="sm"
                className="btn-modern btn-primary-modern px-6 py-2"
              >
                {isRetrying ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Tentando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Tentar Novamente
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // console.log('🎬 [ENHANCED_HERO] Rendering with events:', events.length, 'Current slide:', currentSlide);

  return (
    <section className="relative h-[40vh] sm:h-[45vh] lg:h-[50vh] min-h-[300px] max-h-[500px] overflow-hidden mt-18">
      {/* Image Analysis - Only for images that should be analyzed */}
      {eventsToAnalyze.map((event, index) => (
        event.imageUrl && (
          <AdvancedImageAnalyzer
            key={`analyzer-${event.imageUrl}`} // Use imageUrl como key para evitar re-análises
            src={event.imageUrl}
            onAnalysisComplete={(result) => handleImageAnalysis(event.imageUrl, result)}
            silentMode={true}
          />
        )
      ))}

      {/* Enhanced Carousel Container */}
      <div className="absolute inset-0">
        {events.map((event, index) => (
          <div
            key={`slide-${event.imageUrl || index}`} // Use imageUrl como key estável
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
              index === currentSlide 
                ? 'opacity-100 scale-100' 
                : 'opacity-0 scale-105'
            } ${!reducedMotion ? 'hero-parallax-layer' : ''}`}
          >
            {/* Background Image */}
            <div className="w-full h-full aspect-event-hero relative">
              <SmartImage
                src={event.imageUrl}
                alt={ApiService.extractEventNameFromUrl(event.eventUrl)}
                containerClassName="w-full h-full"
                sizes="100vw"
                loading={index === 0 ? "eager" : "lazy"}
                mode="auto"
                blurIntensity="strong"
                onImageAnalyzed={(metadata) => {
                  // console.log(`🎬 [ENHANCED_HERO] Image ${index + 1} analyzed:`, {
                  //   event: ApiService.extractEventNameFromUrl(event.eventUrl).slice(0, 30),
                  //   type: metadata.type,
                  //   needsBlur: metadata.needsBlur,
                  //   dimensions: `${metadata.width}x${metadata.height}`,
                  //   aspectRatio: metadata.aspectRatio.toFixed(2)
                  // });
                }}
              />
            </div>
            
            {/* OVERLAY ALARANJADO #FF6600 COM 50% OPACIDADE E BLEND MODE OVERLAY */}
            <div 
              className="absolute inset-0"
              style={{
                backgroundColor: '#FF6600',
                opacity: 0.5,
                mixBlendMode: 'overlay'
              }}
            />
            
            {/* Dynamic Adaptive Gradients based on Image Analysis */}
            <div className={`absolute inset-0 ${dynamicOverlayClasses}`} />
            
            {/* Enhanced Text Area Gradient */}
            <div className="absolute inset-0 hero-text-area-gradient" />
          </div>
        ))}
      </div>

      {/* Enhanced Navigation - Only show if more than 1 event */}
      {events.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 hero-nav-button text-white p-2 sm:p-3 rounded-full transition-smooth backdrop-blur-md z-20 shadow-lg"
            aria-label="Evento anterior"
          >
            <ChevronLeft size={20} className="sm:w-6 sm:h-6" />
          </button>
          
          <button
            onClick={goToNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 hero-nav-button text-white p-2 sm:p-3 rounded-full transition-smooth backdrop-blur-md z-20 shadow-lg"
            aria-label="Próximo evento"
          >
            <ChevronRight size={20} className="sm:w-6 sm:h-6" />
          </button>

          {/* Slide Indicators - Bottom Left */}
          <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 z-20">
          </div>

          {/* View Event Button - Bottom Center */}
          <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-20">
            <Button 
              size="default" 
              className="hero-button-style text-white px-6 py-3 transition-smooth transform hover:scale-105 shadow-lg hover:shadow-glow"
              onClick={() => currentEvent && handleEventClick(currentEvent.eventUrl)}
              disabled={!currentEvent}
            >
              Ver Evento
              <ExternalLink className="ml-2" size={18} />
            </Button>
          </div>

          {/* Enhanced Progress Bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40 z-20">
            <div 
              className={`h-full bg-gradient-to-r from-orange-primary to-orange-primary/80 transition-all shadow-glow ${
                isAutoPlaying && !reducedMotion ? 'duration-[6000ms] ease-linear' : 'duration-0'
              }`}
              style={{ 
                width: isAutoPlaying && !reducedMotion ? '100%' : '0%',
                transitionDuration: isAutoPlaying && !reducedMotion ? '6000ms' : '0ms'
              }}
            />
          </div>
        </>
      )}

      {/* Enhanced Visual Effects */}
      <div className="absolute inset-0 pointer-events-none z-5">
        {/* Adaptive Vignette */}
        <div className="absolute inset-0 hero-vignette" />
        
        {/* Floating Particles - Respect motion preferences */}
        {!reducedMotion && (
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full hero-float-particle"
                style={{
                  left: `${20 + i * 20}%`,
                  top: `${30 + i * 15}%`,
                  animationDelay: `${i * 1.2}s`,
                  animationDuration: `${4 + (i % 2)}s`
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// Memoize o componente inteiro
export const EnhancedHero = memo(EnhancedHeroComponent);